/**
 * Watchlist API client
 */

import { apiClient, apiRequest } from './client';
import { NotesApiResponse, StockNote, StockDetails, WatchlistInfo } from '@/types/watchlist';

export interface Watchlist {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
  number_of_stocks?: number;
  watchlist_type?: 'default' | 'system_generated';
}

export interface Stock {
  isin: string;
  name?: string;
  industry?: string;
  added_at?: string;
  nse_code?: string;
  bse_code?: string;
  stock_code?: string;
  trading_view_navlink?: string;
  screener_navlink?: string;
}

export interface AllStock {
  isin: string;
  name: string;
  industry: string;
  nse_code?: string;
  bse_code?: string;
  stock_code?: string;
  trading_view_navlink?: string;
  screener_navlink?: string;
}

interface ApiResponse<T> {
  status: 'success' | 'error';
  data?: T;
  message?: string;
}

export const watchlistApi = {
  listWatchlists: async (): Promise<{ watchlists: Watchlist[] }> => {
    const response = await apiClient.get('/watchlist/watchlists/');
    const apiResponse = response.data as ApiResponse<{ watchlists: Watchlist[] }>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to load watchlists');
  },

  createWatchlist: async (name: string): Promise<Watchlist> => {
    const response = await apiClient.post('/watchlist/watchlists/create/', { name });
    const apiResponse = response.data as ApiResponse<Watchlist>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to create watchlist');
  },

  updateWatchlist: async (watchlistId: string, name: string): Promise<Watchlist> => {
    const response = await apiRequest<ApiResponse<Watchlist>>(`/watchlist/watchlists/${watchlistId}/`, {
      method: 'PUT',
      body: { name }
    });
    if (response.status === 'success' && response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to update watchlist');
  },

  deleteWatchlist: async (watchlistId: string): Promise<void> => {
    await apiRequest(`/watchlist/watchlists/${watchlistId}/delete/`, {
      method: 'DELETE'
    });
  },

  listStocks: async (watchlistId: string): Promise<{ stocks: Stock[] }> => {
    const response = await apiClient.get(`/watchlist/watchlists/${watchlistId}/stocks/`);
    const apiResponse = response.data as ApiResponse<{ stocks: Stock[] }>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to load stocks');
  },

  addStock: async (watchlistId: string, isin: string): Promise<Stock> => {
    const response = await apiClient.post(`/watchlist/watchlists/${watchlistId}/stocks/add/`, { isin });
    const apiResponse = response.data as ApiResponse<Stock>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to add stock');
  },

  removeStock: async (watchlistId: string, isin: string): Promise<void> => {
    await apiRequest(`/watchlist/watchlists/${watchlistId}/stocks/${isin}/delete/`, {
      method: 'DELETE'
    });
  },

  addNote: async (isin: string, note: string): Promise<void> => {
    await apiClient.post(`/watchlist/watchlists/notes/add/`, { isin, note });
  },

  getNotes: async (isin: string): Promise<{ notes: StockNote[]; stockDetails: StockDetails; stockPresentInWatchlist: WatchlistInfo[] }> => {
    const response = await apiClient.get(`/watchlist/watchlists/notes/${isin}/`);
    const apiResponse = response.data as NotesApiResponse;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return {
        notes: apiResponse.data.notes,
        stockDetails: apiResponse.data.stock_details,
        stockPresentInWatchlist: apiResponse.data.stock_present_in_watchlist || []
      };
    }
    throw new Error(apiResponse.message || 'Failed to load notes');
  },

  getCommonStocks: async (): Promise<{ stocks: Stock[] }> => {
    const response = await apiClient.get('/watchlist/analytics/common-stocks/');
    const apiResponse = response.data as ApiResponse<{ stocks: Stock[] }>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to load common stocks');
  },

  getIntersectionStocks: async (watchlistIds: string[]): Promise<{ stocks: Stock[] }> => {
    const response = await apiClient.get('/watchlist/analytics/intersection-stocks/', {
      params: { watchlist_ids: watchlistIds.join(',') }
    });
    const apiResponse = response.data as ApiResponse<{ stocks: Stock[] }>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to load intersection stocks');
  },

  getRecentStocks: async (): Promise<{ stocks: Stock[] }> => {
    const response = await apiClient.get('/watchlist/analytics/recent-stocks/');
    const apiResponse = response.data as ApiResponse<{ stocks: Stock[] }>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    throw new Error(apiResponse.message || 'Failed to load recent stocks');
  },

  getAllStocks: async (): Promise<AllStock[]> => {
    const response = await apiClient.get('/market/all-stocks');
    const apiResponse = response.data as ApiResponse<AllStock[]>;
    if (apiResponse.status === 'success' && apiResponse.data) {
      return apiResponse.data;
    }
    return response.data as AllStock[];
  }
}; 